import React, { type ReactNode, Suspense, useEffect } from 'react';

import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Pressable from '@atlaskit/primitives/pressable';

import { onSearchDialogErrorShown } from '../../../../common/utils/quick-find/events/search-dialog-error-shown';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../controllers/quick-find/analytics';

import { messages } from './messages';

const Image = React.lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/search-error-image" */ './image'
		),
);

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 'space.200',
	color: 'color.text',
});

const imageFallbackStyles = xcss({
	width: '124px',
	height: '124px',
});

const headingStyles = xcss({
	font: 'font.heading.small',
	fontWeight: 'font.weight.medium',
	marginTop: 'space.100',
});

const bodyStyles = xcss({
	marginTop: 'space.100',
	font: 'font.body',
});

const pressableStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	font: 'font.body',
	color: 'color.link',
	padding: 'space.0',
	':hover': {
		textDecoration: 'underline',
		cursor: 'pointer',
	},
});

type ErrorScreenProps = {
	onRetry: () => void;
};

export const ErrorScreen = ({ onRetry }: ErrorScreenProps): React.JSX.Element => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	useEffect(() => {
		fireAnalyticsEvent(
			onSearchDialogErrorShown({ attributes: commonAttributes, nonPrivacySafeAttributes }),
		);
		// Same as other analytics events, we only want this to fire when the screen displays
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box xcss={containerStyles}>
			<Suspense fallback={<Box xcss={imageFallbackStyles} />}>
				{/* eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details */}
				<Image />
			</Suspense>
			<Box xcss={headingStyles}>
				<FormattedMessage {...messages.errorSearchHeading} />
			</Box>
			<Box xcss={bodyStyles}>
				<FormattedMessage
					{...messages.errorSearchBody}
					values={{
						button: (s: ReactNode[]) => (
							<Pressable xcss={pressableStyles} onClick={onRetry}>
								{s}
							</Pressable>
						),
					}}
				/>
			</Box>
		</Box>
	);
};
